// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import {
    createdAtField,
    getCreatedAtSort,
    idField,
    payfactoMerchantNoField,
    paymentModeField,
} from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import Organization from './Organization';
import User from './User';
import LocationSession from './LocationSession';
import OrderedItem from './OrderedItem';
import OrgLocation from './OrgLocation';
import PaymentMethod from './PaymentMethod';
import BillTax from './BillTax';
import Transaction from './Transaction';

class Bill implements StoredEntity {
    static classInternalName = 'bill';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        status: {
            displayName: 'Status',
            recordName: 'status',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    1: 'Open',
                    2: 'Closed',
                    3: 'Canceled',
                    4: 'Completed',
                    5: 'Manual',
                    6: 'Completed (Manual)',
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        items: {
            displayName: 'Items',
            recordName: 'items',
            dataType: {
                primitive: 'simple-entity',
                isArray: true,
                extraData: {
                    model: () => OrderedItem,
                },
            },
            supportsQuery: false,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        taxes: {
            displayName: 'Taxes',
            recordName: 'taxes',
            dataType: {
                primitive: 'simple-entity',
                isArray: true,
                extraData: {
                    model: () => BillTax,
                },
            },
            supportsQuery: false,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        subtotal: {
            displayName: 'Subtotal',
            recordName: 'subtotal',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        total: {
            displayName: 'Total',
            recordName: 'total',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        rewardsDiscount: {
            displayName: 'Rewards Discount',
            recordName: 'rewards_discount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        discountMoney: {
            displayName: 'Discount $',
            recordName: 'discount_money',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        tipPercent: {
            displayName: 'Tip %',
            recordName: 'tip_percent',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        tipAmount: {
            displayName: 'Tip $',
            recordName: 'tip_amount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        tipIsCustom: {
            displayName: 'Custom Tip?',
            recordName: 'tip_is_custom',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        organizationId: {
            displayName: 'Organization',
            recordName: 'organization_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Organization,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        locationId: {
            displayName: 'Location',
            recordName: 'location_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => OrgLocation,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        userId: {
            displayName: 'User',
            recordName: 'user_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => User,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        locationSessionId: {
            displayName: 'Session',
            recordName: 'location_session_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => LocationSession,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        posTableNr: {
            displayName: 'POS Table #',
            recordName: 'pos_table_nr',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        posCustomerNr: {
            displayName: 'POS Customer #',
            recordName: 'pos_customer_nr',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        posServer: {
            displayName: 'POS Server',
            recordName: 'pos_server',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        posBillNr: {
            displayName: 'POS Bill #',
            recordName: 'pos_bill_nr',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        posAddress: {
            displayName: 'POS Address',
            recordName: 'pos_address',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        posLocationName: {
            displayName: 'POS Location Name',
            recordName: 'pos_location_name',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        posDate: {
            displayName: 'POS Date',
            recordName: 'pos_date',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        posHour: {
            displayName: 'POS Hour',
            recordName: 'pos_hour',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        paymentMethodId: {
            displayName: 'Payment Method',
            recordName: 'payment_method_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => PaymentMethod,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        insuredAmount: {
            displayName: 'Insured Amount',
            recordName: 'insured_amount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        rewardsAwarded: {
            displayName: 'Rewards Awarded',
            recordName: 'rewards_awarded',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        shouldAward: {
            displayName: 'Eligible for Rewards',
            recordName: 'should_award',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        orgRewardThreshold: {
            displayName: 'Organization Reward Threshold',
            recordName: 'org_reward_threshold',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        orgRewardAmount: {
            displayName: 'Organization Reward Amount',
            recordName: 'org_reward_amount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        assistanceRequired: {
            displayName: 'Assistance Required',
            recordName: 'assistance_required',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        preAuthFailed: {
            displayName: 'Pre Auth Failed',
            recordName: 'pre_auth_failed',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        chargeFailed: {
            displayName: 'Charge Failed',
            recordName: 'charge_failed',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        failedAuthId: {
            displayName: 'Failed Auth',
            recordName: 'failed_auth_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Transaction,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        failedCaptureId: {
            displayName: 'Failed Capture',
            recordName: 'failed_capture_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Transaction,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        userChangeCutoffTime: {
            displayName: 'User Change Cutoff Time',
            recordName: 'user_change_cutoff_time',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        pmChanging: {
            displayName: 'Payment Method Change in Progress',
            recordName: 'pm_changing',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        subDiscount: {
            displayName: 'Sign Up Bonus Discount',
            recordName: 'discount_sub',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        subThreshold: {
            displayName: 'Sign Up Bonus Threshold',
            recordName: 'sub_threshold',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        subAmount: {
            displayName: 'Sign Up Bonus Amount',
            recordName: 'sub_amount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        subEnabled: {
            displayName: 'Sign Up Bonus Applicable',
            recordName: 'sub_enabled',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        isApproximate: {
            displayName: 'Is Approximate',
            recordName: 'is_approximate',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        authId: {
            displayName: 'Auth',
            recordName: 'auth_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Transaction,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        captureId: {
            displayName: 'Capture',
            recordName: 'capture_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => Transaction,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        paymentMode: paymentModeField,
        payfactoMerchantNo: payfactoMerchantNoField,
    };

    static fileFields: EntityFieldCollection = {};

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[Bill.fields.id.recordName];
    }

    get displayName() {
        return this.record[Bill.fields.id.recordName];
    }

    static endpoints: Endpoints = constructEndpoints('bills');

    constructor(record: any) {
        this.record = record;
    }
}

export default Bill;
